import { isSameDay } from 'date-fns';
import { useMemo, useRef, useState } from 'react';
import { formatDateRange, formatToUSFullDate } from 'utils/dateTimeUtils/dateFormatUtil';
import {
    addMonthsToDate,
    isDateAfter,
    isDateBefore,
    isDateValid,
    parseDateString,
    validateDateRange
} from 'utils/dateTimeUtils/dateValidationUtil';

import { useEvent } from '@american-autonomy/ag-ui';

import { COMPLETE_DATE_LENGTH, DATE_FORMAT } from './constants';
import type { DateRange, Props, RangeMapKeys } from './types';

const INIT_RANGE: DateRange = { from: undefined, to: undefined };

const useDateRangePicker = ({ onChange, popoverRef, rangeInterval, refs, value }: Props) => {
    const { endInputRef, startInputRef } = refs;
    const containerRef = useRef<HTMLDivElement>(null);
    const [selectedDateRange, setSelectedDateRange] = useState<DateRange>(value || INIT_RANGE);
    const [isSelectingEnd, setIsSelectingEnd] = useState(false);

    const [startInputValue, setStartInputValue] = useState<string | undefined>(undefined);
    const [endInputValue, setEndInputValue] = useState<string | undefined>(undefined);

    const inputsRefMap = useMemo(
        () => ({
            end: endInputRef,
            start: startInputRef
        }),
        [endInputRef, startInputRef]
    );

    const showPopover = () => {
        const element = popoverRef.current as HTMLElement & { showPopover?: VoidFunction };

        element?.showPopover?.();
    };

    const hidePopover = () => {
        const element = popoverRef.current as HTMLElement & { hidePopover?: VoidFunction };

        element?.hidePopover?.();
    };

    const handleClickOutside = (event: Event) => {
        const target = event.target as HTMLElement;
        const isInsideDropdown = popoverRef.current?.contains(target);
        const isInsideInputs = containerRef.current?.contains(target);

        if (!isInsideDropdown && !isInsideInputs) {
            hidePopover();
        }
    };

    useEvent({ event: 'mousedown', handler: handleClickOutside });

    const reset = () => {
        onChange?.(INIT_RANGE);
        setSelectedDateRange(INIT_RANGE);
        setStartInputValue(undefined);
        setEndInputValue(undefined);
        setIsSelectingEnd(false);
        hidePopover();
    };

    const handleBlur = () => {
        const startLength = startInputRef.current?.value.length ?? 0;
        const endLength = endInputRef.current?.value.length ?? 0;

        const isStartStable = startLength === 0 || startLength === COMPLETE_DATE_LENGTH;
        const isEndStable = endLength === 0 || endLength === COMPLETE_DATE_LENGTH;

        if (isStartStable) {
            if (selectedDateRange.from && isDateValid(selectedDateRange.from)) {
                // Restore to the last valid selected date.
                setStartInputValue(formatToUSFullDate(selectedDateRange.from));
            } else {
                // No valid selected date — clear only if the typed value is itself
                // an invalid (unparseable) date. Valid-but-out-of-interval dates are
                // left as-is so that the user can complete a manual range entry.
                const currentStartValue = startInputRef.current?.value ?? '';
                const parsedStart = parseDateString(currentStartValue, DATE_FORMAT);

                if (!isDateValid(parsedStart)) {
                    setStartInputValue(undefined);
                }
            }
        }

        if (isEndStable) {
            if (selectedDateRange.to && isDateValid(selectedDateRange.to)) {
                setEndInputValue(formatToUSFullDate(selectedDateRange.to));
            } else {
                const currentEndValue = endInputRef.current?.value ?? '';
                const parsedEnd = parseDateString(currentEndValue, DATE_FORMAT);

                if (!isDateValid(parsedEnd)) {
                    setEndInputValue(undefined);
                }
            }
        }
    };

    const handleChange = (date?: DateRange) => {
        if (!date) {
            setSelectedDateRange(INIT_RANGE);
            setStartInputValue(undefined);
            setEndInputValue(undefined);
            setIsSelectingEnd(false);

            return;
        }

        if (date.from && date.to && isSameDay(date.from, date.to) && !isSelectingEnd) {
            // First calendar click on an empty picker: DayPicker (min=0) fires
            // {from: day, to: day}. Capture the start and wait for the second
            // click (the "end" selection) before completing the range.
            const startDate = date.from;

            setSelectedDateRange({ from: startDate, to: undefined });
            setStartInputValue(formatToUSFullDate(startDate));
            setEndInputValue(undefined);
            setIsSelectingEnd(true);

            if (startInputRef.current) {
                startInputRef.current.value = formatToUSFullDate(startDate);
            }

            return;
        }

        setIsSelectingEnd(false);

        const startDate = date.from ? new Date(date.from) : undefined;
        const endDate = date.to ? new Date(date.to) : undefined;

        startDate?.setHours(0);
        endDate?.setHours(23);

        const range: DateRange = { from: startDate, to: endDate };
        const { from, to } = formatDateRange(range);

        setSelectedDateRange(range);

        if (from) {
            setStartInputValue(from);

            if (startInputRef.current) {
                startInputRef.current.value = from;
            }
        }

        if (to) {
            setEndInputValue(to);

            if (endInputRef.current) {
                endInputRef.current.value = to;
            }
        }

        if (from && to) {
            hidePopover();
            onChange?.(range);
        }
    };

    const handleTextChange = () => {
        const startValue = startInputRef.current?.value ?? '';
        const endValue = endInputRef.current?.value ?? '';

        setStartInputValue(startValue || undefined);
        setEndInputValue(endValue || undefined);

        const isStartEmpty = !startValue;
        const isEndEmpty = !endValue;
        const isStartComplete = startValue.length === COMPLETE_DATE_LENGTH;
        const isEndComplete = endValue.length === COMPLETE_DATE_LENGTH;

        const isStartStable = isStartEmpty || isStartComplete;
        const isEndStable = isEndEmpty || isEndComplete;

        if (!isStartStable || !isEndStable) {
            return;
        }

        const parsedFrom = isStartComplete ? parseDateString(startValue, DATE_FORMAT) : undefined;
        const parsedTo = isEndComplete ? parseDateString(endValue, DATE_FORMAT) : undefined;

        if ((parsedFrom && !isDateValid(parsedFrom)) || (parsedTo && !isDateValid(parsedTo))) {
            return;
        }

        parsedFrom?.setHours(0);
        parsedTo?.setHours(23);

        if (parsedFrom && parsedTo) {
            const validatedRange = validateDateRange({ from: parsedFrom, to: parsedTo });

            if (!validatedRange) {
                return;
            }

            setSelectedDateRange(validatedRange);
            onChange?.(validatedRange);
            hidePopover();

            return;
        }

        if (!parsedFrom && !parsedTo) {
            setSelectedDateRange(INIT_RANGE);
            onChange?.(INIT_RANGE);

            return;
        }

        setSelectedDateRange({ from: parsedFrom, to: parsedTo });
    };

    const handleOpen = (inputName: RangeMapKeys) => {
        inputsRefMap[inputName].current?.focus();
        showPopover();
    };

    const disableDaysFromRange = (day: Date) => {
        if (!selectedDateRange?.from && !selectedDateRange?.to) {
            return false;
        }

        const { from, to } = selectedDateRange;
        const fromDateMax = from && addMonthsToDate(from, 6);
        const toDateMax = to && addMonthsToDate(to, -6);

        if (fromDateMax && isDateAfter(day, fromDateMax)) {
            return true;
        }

        if (toDateMax && isDateBefore(day, toDateMax)) {
            return true;
        }

        return false;
    };

    const dayPickerInterval = useMemo(
        () => ({
            after: rangeInterval.end as Date,
            before: rangeInterval.start as Date
        }),
        [rangeInterval]
    );

    return {
        containerRef,
        dayPickerInterval,
        disableDaysFromRange,
        endInputValue,
        handleBlur,
        handleChange,
        handleOpen,
        handleTextChange,
        reset,
        selectedDateRange,
        startInputValue
    };
};

export default useDateRangePicker;
